import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './App.css';
import { DevSiteEntryMenu } from './DevSiteEntryMenu';
import { EmailGate } from './EmailGate';
import {
  buildGuestVoiceNoteHref,
  buildTypeformRsvpReplyHref,
  clearVerifiedGuestStorage,
  isRemoteGuestRegistryConfigured,
  isRemoteGuestRegistryHydrated,
  persistDevBypassGuest,
  readDevSiteVisibilityMode,
  readVerifiedGuestFromStorage,
  resolveEffectiveSiteVisibility,
  isCeremonyPlusSiteVisibility,
  SITE_VISIBILITY,
  WEDDING_GUEST_REGISTRY_CHANGE_EVENT,
  writeDevSiteVisibilityMode,
} from './guestAllowlist';
import { startRemoteGuestRegistryListener } from './firebaseRemoteSync';
import {
  getVoiceNotePathname,
  isAdminPathname,
  isHoneymoonFundPathname,
  isVoiceNotePathname,
  WEDDING_APP_PATH_CHANGED_EVENT,
} from './adminPath';
import { ProductionGuestAdmin } from './ProductionGuestAdmin';
import { VoiceNotePage } from './VoiceNotePage';
import { HotelsModal } from './HotelsModal';
import { StayTodoModal } from './StayTodoModal';
import { TaxisModal } from './TaxisModal';
import { preloadWeddingSiteAssets } from './sitePreload';
import { SplashScotlandScript } from './SplashScotlandScript';
import { HeroFireworks } from './HeroFireworks';

const TRAVEL_MAPS_URL = 'https://maps.app.goo.gl/qmSFSjRXTV1tmbvUA';

const WEDDING_MONZO_ME_URL =
  'https://monzo.me/benjaminsilvertown?h=3Hweok&account_type=joint';

/** Order matches page flow — scroll-spy picks the last section whose top is above the nav offset line */
const NAV_SECTION_IDS = ['about', 'travel', 'coach-travel', 'stay', 'faqs', 'rsvp'];

function getNavSectionIds(includeCoachTravel) {
  if (includeCoachTravel) return NAV_SECTION_IDS;
  return NAV_SECTION_IDS.filter((id) => id !== 'coach-travel');
}

function getNavScrollOffset() {
  if (typeof window === 'undefined') return 72;
  const nav = document.querySelector('.site-nav');
  return nav ? Math.round(nav.getBoundingClientRect().height + 12) : 72;
}

function getScrollY() {
  if (typeof window === 'undefined') return 0;
  return (
    window.scrollY ??
    window.pageYOffset ??
    document.documentElement.scrollTop ??
    0
  );
}

function computeActiveNavHash(sectionIds) {
  const ids = sectionIds ?? NAV_SECTION_IDS;
  if (typeof window === 'undefined') return `#${ids[0]}`;
  const scrollY = getScrollY();
  const navH = getNavScrollOffset();
  const vh = window.innerHeight || 0;
  /*
   * Document Y of the “reading” line: below the sticky nav, but not at the very top edge.
   * Using only scrollY + navH waits until each section’s top hits the nav—so Stay stayed
   * active while FAQ was already on screen. Bias the line ~⅓ down the viewport so the next
   * section activates as soon as its content enters view.
   */
  const probeDocY = scrollY + Math.max(navH, vh * 0.32);
  let active = ids[0];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top + scrollY;
    if (top <= probeDocY + 1) active = id;
  }
  return `#${active}`;
}

const sectionArt = {
  hoverFlower: `${process.env.PUBLIC_URL || ''}/assets/hover-flower.svg`,
  travelTitle: `${process.env.PUBLIC_URL || ''}/assets/travel.svg`,
  travelTrain: `${process.env.PUBLIC_URL || ''}/assets/train.svg`,
  travelPlane: `${process.env.PUBLIC_URL || ''}/assets/plane.svg`,
  splashScrollFlower: '/assets/scroll-flower.svg',
  splashMonogram: '/assets/mb-monogram.png',
  splashPhoto: '/assets/thumbnail-strip.gif',
  heroTop: '/assets/join-us-on.svg',
  heroTitle: '/assets/new-years-eve.svg',
  heroBottom: '/assets/to-celebrate.svg',
  penicuikCourtyard: '/assets/penicuik-courtyard.jpg',
  theReception: '/assets/the-reception.svg',
  /** On-site guests only; add master to assets/ and public/assets/ (VIP-ticket.png). */
  vipTicket: `${process.env.PUBLIC_URL || ''}/assets/VIP-ticket.png`,
  theCeremony: '/assets/the-ceremony.svg',
  notebookPaper: '/assets/notebook-paper.png',
  blueNotebookPaper: '/assets/blue-paper.png',
  redPaper: `${process.env.PUBLIC_URL || ''}/assets/red_paper.png`,
  weddingCoaches: `${process.env.PUBLIC_URL || ''}/assets/wedding-coaches.svg`,
  stay: `${process.env.PUBLIC_URL || ''}/assets/stay.svg`,
  hotels: `${process.env.PUBLIC_URL || ''}/assets/hotels.svg`,
  todo: `${process.env.PUBLIC_URL || ''}/assets/todo.svg`,
  pleaseRsvp: `${process.env.PUBLIC_URL || ''}/assets/please-rsvp.svg`,
  beforeWePanic: `${process.env.PUBLIC_URL || ''}/assets/before-we-panic.svg`,
  faqTitle: `${process.env.PUBLIC_URL || ''}/assets/faq-title.svg`,
  faqMarker: `${process.env.PUBLIC_URL || ''}/assets/faq-marker.svg`,
  faqMarkerStayAccent: `${process.env.PUBLIC_URL || ''}/assets/faq-marker-stay-accent.svg`,
  faqDivider: `${process.env.PUBLIC_URL || ''}/assets/divider.svg`,
  splitter: `${process.env.PUBLIC_URL || ''}/assets/splitter.svg`,
  painterTape: `${process.env.PUBLIC_URL || ''}/assets/painter-tape.png`,
  /** Public URL so email-gate preload can warm the same asset modals use (was bundled import). */
  paperTexture: `${process.env.PUBLIC_URL || ''}/assets/paper-texture.jpg`,
  footerBg: `${process.env.PUBLIC_URL || ''}/assets/footer.png`,
  footerWithLove: `${process.env.PUBLIC_URL || ''}/assets/withlove.svg`,
};

const weddingDetails = {
  heroInviteReceptionOnly: `The wedding of
Megan and Benjamin

Penicuik Estate
Midlothian, Scotland
31.12.26

3:30pm until 12:30 a.m.
Black tie`,
  heroInviteCeremonyAndReception: `The wedding of
Megan and Benjamin

Penicuik Estate
Midlothian, Scotland
31.12.26

Arrive 12pm
12:30 a.m. carriages
Black tie`,
  /** On-site C+ hero: three 16pt intro <p>s (pre-line); 12pt schedule days; splitter between. */
  heroPenicuikOnSiteInvite: {
    introParagraphs: [
      'The wedding of\nMegan and Benjamin',
      'Penicuik Estate\nMidlothian, Scotland',
      '30.12.26 - 01.01.27',
    ],
    schedule: [
      { day: 'Weds 30th Dec', lines: ['Check in from 1pm'] },
      { day: 'Thurs 31st Dec', lines: ['Wedding from 12pm', 'Black tie'] },
      { day: 'Fri 1st Jan', lines: ['Check out 2pm'] },
    ],
  },
  estateStoryReceptionOnly: [
    'Meg & Ben will exchange vows in a small ceremony with close family and friends.',
    'It would mean the world to celebrate with you afterwards for the reception, dinner, and to welcome in the New Year together.',
  ],
  estateStoryCeremonyAndReception: [
    'Meg & Ben will exchange vows with their closest family and friends. You are one of them.',
    'After, we will be joined by all, for the reception, dinner, and to welcome in the New Year together.',
  ],
  travelAddress: ['Penicuik House', 'Penicuik Estate, Carlops Rd,', 'Penicuik, Scotland EH26 9LA'],
  travelModes: [
    {
      key: 'train',
      body: `4:30h from
King's Cross London to
Edinburgh Waverley`,
    },
    {
      key: 'plane',
      body: `1hr 25mins from
London to
Edinburgh Airport (EDI)`,
    },
  ],
  travelFootnoteLines: [
    'Penicuik is a short 20 min drive from the\nairport and 30 mins from Edinburgh Waverley',
    'We will be travelling up by train on 29 December. We can block-book a carriage for anyone who would like to join us. Please let us know.',
  ],
  coachTravelReceptionOnly: {
    intro:
      'We are arranging return transport from The Meadows, Edinburgh to Penicuik Estate.',
    trips: [
      { route: 'The Meadows to Penicuik', time: '3pm' },
      { route: 'Penicuik to The Meadows', time: '10:30pm' },
      { route: 'Penicuik to The Meadows', time: '12:30am' },
    ],
    note:
      "If you wish to travel outside these times please book taxis in advance. Hogmanay is the busiest night of the year.",
  },
  coachTravelCeremonyAndReception: {
    intro:
      'We are arranging late-night transport from Penicuik Estate to The Meadows, Edinburgh',
    trips: [
      { route: 'Penicuik to The Meadows', time: '10:30pm' },
      { route: 'Penicuik to The Meadows', time: '12:30am' },
    ],
    note:
      "If you wish to travel outside these times please book taxis in advance. Hogmanay is the busiest night of the year.",
  },
  stay: {
    lead:
      "Hogmanay is a magical time in Edinburgh, if you're making a trip of it we have some hotel and event suggestions.",
    detail:
      "We recommend staying near The Meadows (south side of Edinburgh centre). Princes Street closes for Hogmanay. Our coaches will collect and drop off near the Meadows.",
  },
  /** Guests with rooms on the estate; same Hotels modal for extra nights nearby. */
  stayPenicuikOnSite: {
    lead:
      "We've arranged on-site accommodation at Penicuik Estate at a reduced rate for guests. We'll be in touch shortly with details and pricing.",
    details: [
      'If you would like to book accommodation elsewhere for extra nights around these dates, please use our hotel suggestions below.',
    ],
  },
  rsvp: {
    deadlineLine: 'by April 30th',
    /** Base Typeform URL; hidden fields filled via #fragment from signed-in guest. */
    replyUrl: 'https://frenzy-app.typeform.com/mb-rsvp',
  },
  faq: [
    {
      question: 'Why Scotland?',
      paragraphs: [
        'It\'s undoubtedly one of our favourite places, which we appreciate sounds delusional to anyone who has stood in horizontal rain on a Highland hillside and questioned the decisions that led them there.',
        '',
        'We got engaged here. We\'ve road tripped with friends, camped, hiked, cold dipped, and fought the weather to a loss more times than we can count. We\'ve eaten our body weight in good food, sat in Fringe audiences (genuinely questioning the will to live) and walked the city with family until our legs gave out. Ben\'s parents are Edinburgh locals, so it already felt like a second home.',
        '',
        'Scotland has given us some of the best moments of our lives, and most of the best stories.',
        '',
        'It felt only right to bring everyone we love here and let it do the same to them.',
      ],
    },
    {
      question: 'Will the ceremony be held outside?',
      paragraphs: [
        'Weather permitting the ceremony will take place outside in the courtyard.',
        'We have an indoor back-up and are expecting all four seasons, possibly within the same hour.',
      ],
    },
    {
      question: 'Are Children allowed?',
      answer:
        'We’re keeping the ceremony and reception to adults only. The only children attending will be those included in the wedding party. We hope you understand and we’d love to celebrate with your family another time.',
    },
    {
      question: 'What time are the coaches?',
      answer:
        'We have pre-arranged coaches for those staying in Edinburgh to easily get to and from Penicuik. The pick up is 2:30pm and the return journey will be 12:30 a.m. If you wish to travel outside of these times please use the local taxi booking suggestions provided above.',
    },
    {
      question: 'What is the dress code?',
      paragraphs: [
        'Elegant Black Tie. Think glamour, but with enough survival instinct to make it through a Scottish evening with all your toes intact.',
        '',
        'Ladies:',
        'Cocktail dresses or evening wear.',
        '',
        'Gentlemen:',
        'Tuxedos or creative black tie.',
        '',
        'Scots:',
        'Highland dress encouraged and tartan admired',
        '',
        'Fashion icons:',
        'The runway starts at the car park. Do what you were born to do.',
        '',
        'Everyone:',
        'Bring a stylish warm overcoat or layers. Looking incredible is the goal. Hypothermia is not.',
      ],
    },
    {
      question: 'Do you have a registry or gift list?',
      paragraphs: [
        'Your being there is genuinely all we could ask for. Please don’t feel any pressure around gifts. Instead, we’d love for you to leave us a little voice recording that we can treasure long after the night is over.',
        '',
        'If you’d like to give something, you’re warmly welcome to contribute to our honeymoon fund.',
      ],
    },
  ],
  /** Appended under the coaches FAQ for Penicuik on-site guests only. */
  faqCoachesOnSiteGuestNote:
    "If you're staying on site: coaches are being organised for guests staying in the city who are attending the reception. Please arrange your own travel to and from the venue on check-in and check-out days.",
  ceremonySchedule: [
    { eyebrow: 'Arrive 12pm', lines: ['Welcome drinks and canapés'] },
    { eyebrow: '1:00pm', lines: ['Ceremony'], subline: 'close family and friends' },
  ],
  /** On-site guests: no “Arrive” (already at Penicuik). */
  ceremonyScheduleOnSite: [
    { eyebrow: '12pm', lines: ['Welcome drinks and canapés'] },
    { eyebrow: '1:00pm', lines: ['Ceremony'], subline: 'close family and friends' },
  ],
  receptionSchedule: [
    { eyebrow: 'Arrive 3:30pm', lines: ['Welcome drinks'] },
    { eyebrow: '4:30pm', lines: ['Seated for dinner'] },
    { eyebrow: '7:30pm', lines: ['Music, moderate', 'elegance, questionable', 'dancing'] },
    {
      eyebrow: '12:00 a.m.',
      lines: ['First footing and bring\u00A0in the New Year'],
    },
    { closing: '12:30 a.m. carriages' },
  ],
  /** Same as receptionSchedule but first block clarifies 3:30pm is for all guests (C+ already had ceremony). */
  receptionScheduleCeremonyPlus: [
    { eyebrow: 'All guests arrive 3:30pm', lines: ['Welcome drinks'] },
    { eyebrow: '4:30pm', lines: ['Seated for dinner'] },
    { eyebrow: '7:30pm', lines: ['Music, moderate', 'elegance, questionable', 'dancing'] },
    {
      eyebrow: '12:00 a.m.',
      lines: ['First footing and bring\u00A0in the New Year'],
    },
    { closing: '12:30 a.m. carriages' },
  ],
  /** Reception notebook closing line for Penicuik on-site guests only (day guests leaving; stayers remain). */
  receptionScheduleClosingOnSite: '12:30 a.m. Day guests depart',
};

function RsvpSection({ verifiedGuest }) {
  const { deadlineLine, replyUrl } = weddingDetails.rsvp;
  const replyHref = useMemo(
    () => buildTypeformRsvpReplyHref(replyUrl, verifiedGuest),
    [replyUrl, verifiedGuest],
  );
  return (
    <section className="rsvp-section" id="rsvp" aria-label="RSVP">
      <div className="rsvp-section__inner">
        <div className="rsvp-section__stack">
          <img
            src={sectionArt.pleaseRsvp}
            alt="Please RSVP"
            className="rsvp-section__title-art"
            width="292"
            height="219"
            decoding="async"
          />
          <p className="rsvp-section__deadline">{deadlineLine}</p>
          <div className="rsvp-section__cta-wrap">
            <a
              href={replyHref}
              className="coach-travel-paper-stack__taxis-btn rsvp-section__reply-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="coach-travel-paper-stack__taxis-btn__ring" aria-hidden="true">
                <svg
                  className="coach-travel-paper-stack__taxis-btn__svg"
                  viewBox="0 0 240 48"
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                >
                  <ellipse
                    className="coach-travel-paper-stack__taxis-btn__ellipse"
                    cx="120"
                    cy="24"
                    rx="115"
                    ry="21"
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    vectorEffect="nonScalingStroke"
                  />
                </svg>
              </span>
              Reply here
            </a>
            <img
              src={sectionArt.beforeWePanic}
              alt=""
              className="rsvp-section__panic-note"
              width="105"
              height="85"
              decoding="async"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer" aria-label="With love from Meg & Ben">
      <div className="site-footer__inner">
        <div className="site-footer__cloth">
          <img
            src={sectionArt.footerBg}
            alt=""
            className="site-footer__cloth-img"
            width="1374"
            height="1155"
            decoding="async"
            aria-hidden="true"
          />
          <img
            src={sectionArt.footerWithLove}
            alt="With love, Meg & Ben, xxx"
            className="site-footer__art"
            width="299"
            height="166"
            decoding="async"
          />
        </div>
      </div>
    </footer>
  );
}

const VOICE_NOTE_SOURCE_FAQ_REGISTRY = 'faq_registry';

/** Same hand-drawn oval CTA as coach travel / RSVP (anchor or button via `as`). */
function CoachTravelSketchButton({ as: Comp = 'button', children, className = '', ...rest }) {
  return (
    <Comp
      className={['coach-travel-paper-stack__taxis-btn', className].filter(Boolean).join(' ')}
      {...rest}
    >
      <span className="coach-travel-paper-stack__taxis-btn__ring" aria-hidden="true">
        <svg
          className="coach-travel-paper-stack__taxis-btn__svg"
          viewBox="0 0 240 48"
          preserveAspectRatio="none"
          width="100%"
          height="100%"
        >
          <ellipse
            className="coach-travel-paper-stack__taxis-btn__ellipse"
            cx="120"
            cy="24"
            rx="115"
            ry="21"
            fill="transparent"
            stroke="currentColor"
            strokeWidth="1.25"
            vectorEffect="nonScalingStroke"
          />
        </svg>
      </span>
      {children}
    </Comp>
  );
}

const FAQ_COACHES_Q = 'What time are the coaches?';
const FAQ_CEREMONY_OUTSIDE_Q = 'Will the ceremony be held outside?';
const FAQ_REGISTRY_GIFT_Q = 'Do you have a registry or gift list?';

function FaqSection({
  penicuikOnSiteAccommodation = false,
  showCeremonyFaq = false,
  verifiedGuest = null,
}) {
  const items = useMemo(() => {
    let list = weddingDetails.faq;
    if (!showCeremonyFaq) {
      list = list.filter((item) => item.question !== FAQ_CEREMONY_OUTSIDE_Q);
    }
    if (!penicuikOnSiteAccommodation) return list;
    return list.map((item) => {
      if (item.question !== FAQ_COACHES_Q || item.paragraphs) return item;
      return {
        question: item.question,
        answer: weddingDetails.faqCoachesOnSiteGuestNote,
      };
    });
  }, [penicuikOnSiteAccommodation, showCeremonyFaq]);

  const [answerOpen, setAnswerOpen] = useState(() => items.map(() => false));

  useEffect(() => {
    setAnswerOpen((prev) =>
      prev.length === items.length ? prev : items.map(() => false),
    );
  }, [items]);

  const toggleAnswer = (index) => {
    setAnswerOpen((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const voiceNoteFaqHref = useMemo(
    () =>
      buildGuestVoiceNoteHref(getVoiceNotePathname(), verifiedGuest, {
        source: VOICE_NOTE_SOURCE_FAQ_REGISTRY,
      }),
    [verifiedGuest],
  );

  return (
    <>
      <section className="faq-section" id="faqs" aria-label="Frequently asked questions">
      <div className="faq-section__inner">
        <header className="faq-section__header">
          <img
            src={sectionArt.faqTitle}
            alt="FAQ"
            className="faq-section__title-art"
            width="132"
            height="80"
            decoding="async"
          />
        </header>
        <div className="faq-section__list">
          {items.map((item, index) => (
            <article key={item.question} className="faq-section__block">
              <h2 className="faq-section__question-heading">
                <button
                  type="button"
                  className="faq-section__question-btn"
                  id={`faq-q-${index}`}
                  aria-expanded={answerOpen[index]}
                  aria-controls={`faq-a-${index}`}
                  onClick={() => toggleAnswer(index)}
                >
                  <span className="faq-section__question-row">
                    <img
                      src={sectionArt.faqMarker}
                      alt=""
                      className="faq-section__question-marker"
                      width="16"
                      height="5"
                      decoding="async"
                      aria-hidden="true"
                    />
                    <span className="faq-section__question">{item.question}</span>
                    <img
                      src={sectionArt.faqMarker}
                      alt=""
                      className="faq-section__question-marker faq-section__question-marker--flip"
                      width="16"
                      height="5"
                      decoding="async"
                      aria-hidden="true"
                    />
                  </span>
                </button>
              </h2>
              <div
                id={`faq-a-${index}`}
                role="region"
                aria-labelledby={`faq-q-${index}`}
                aria-hidden={!answerOpen[index]}
                className={[
                  'faq-section__item-panel',
                  answerOpen[index] && 'faq-section__item-panel--open',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="faq-section__item-panel-inner">
                  <div className="faq-section__answer-stack">
                    {item.paragraphs ? (
                      <div className="faq-section__answer faq-section__answer--multi">
                        {item.paragraphs.map((line, i) =>
                          line === '' ? (
                            <p key={`${item.question}-sp-${i}`} className="faq-section__answer-gap">
                              &nbsp;
                            </p>
                          ) : (
                            <p key={`${item.question}-${i}`} className="faq-section__answer-line">
                              {line}
                            </p>
                          ),
                        )}
                      </div>
                    ) : (
                      <p className="faq-section__answer">{item.answer}</p>
                    )}
                    {item.question === FAQ_REGISTRY_GIFT_Q ? (
                      <div className="faq-section__gift-actions">
                        <CoachTravelSketchButton as="a" href={voiceNoteFaqHref}>
                          Leave voice note
                        </CoachTravelSketchButton>
                        <CoachTravelSketchButton
                          as="a"
                          href={WEDDING_MONZO_ME_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Honeymoon fund
                        </CoachTravelSketchButton>
                      </div>
                    ) : null}
                    <img
                      src={sectionArt.faqDivider}
                      alt=""
                      className="faq-section__divider faq-section__divider--in-answer"
                      width="18"
                      height="15"
                      decoding="async"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
    </>
  );
}

function StaySectionActionRing() {
  return (
    <span className="travel-section__maps-btn__ring" aria-hidden="true">
      <svg
        className="travel-section__maps-btn__svg"
        viewBox="0 0 240 48"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        <ellipse
          className="travel-section__maps-btn__ellipse"
          cx="120"
          cy="24"
          rx="115"
          ry="21"
          fill="transparent"
          stroke="currentColor"
          strokeWidth="1.25"
          vectorEffect="nonScalingStroke"
        />
      </svg>
    </span>
  );
}

function StaySection({ penicuikOnSiteAccommodation }) {
  const s = penicuikOnSiteAccommodation ? weddingDetails.stayPenicuikOnSite : weddingDetails.stay;
  const [hotelsModalOpen, setHotelsModalOpen] = useState(false);
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const hotelsDialogId = 'stay-hotels-dialog';
  const todoDialogId = 'stay-todo-dialog';
  const stayBtnClass = 'travel-section__maps-btn stay-section__maps-btn';

  return (
    <section className="stay-section" id="stay" aria-label="Stay">
      <HotelsModal
        open={hotelsModalOpen}
        onClose={() => setHotelsModalOpen(false)}
        dialogId={hotelsDialogId}
        faqMarkerSrc={sectionArt.faqMarker}
        faqDividerSrc={sectionArt.faqDivider}
        tierMarkerSrc={sectionArt.faqMarkerStayAccent}
        hotelsTitleArtSrc={sectionArt.hotels}
        tapeArtSrc={sectionArt.painterTape}
        paperTextureSrc={sectionArt.paperTexture}
      />
      <StayTodoModal
        open={todoModalOpen}
        onClose={() => setTodoModalOpen(false)}
        dialogId={todoDialogId}
        faqMarkerSrc={sectionArt.faqMarker}
        todoTitleArtSrc={sectionArt.todo}
        tapeArtSrc={sectionArt.painterTape}
        paperTextureSrc={sectionArt.paperTexture}
      />
      <div className="stay-section__inner">
        <div className="stay-section__intro">
          <header className="stay-section__header">
            <img
              src={sectionArt.stay}
              alt="Stay"
              className="stay-section__title-art"
              width="69"
              height="75"
              decoding="async"
            />
          </header>
          <div className="stay-section__copy">
            <p className="stay-section__lead">{s.lead}</p>
            {penicuikOnSiteAccommodation ? (
              <>
                {s.details.map((para, i) => (
                  <Fragment key={`stay-penicuik-${i}`}>
                    {i > 0 ? (
                      <img
                        src={sectionArt.faqDivider}
                        alt=""
                        className="stay-section__copy-divider"
                        width="18"
                        height="15"
                        decoding="async"
                        aria-hidden="true"
                      />
                    ) : null}
                    <p className="stay-section__detail stay-section__detail--preline">{para}</p>
                  </Fragment>
                ))}
              </>
            ) : (
              <p className="stay-section__detail">{s.detail}</p>
            )}
          </div>
          <div className="stay-section__actions">
            <button
              type="button"
              className={stayBtnClass}
              onClick={() => setHotelsModalOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={hotelsModalOpen}
              aria-controls={hotelsDialogId}
            >
              <StaySectionActionRing />
              Hotels
            </button>
            <button
              type="button"
              className={stayBtnClass}
              onClick={() => setTodoModalOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={todoModalOpen}
              aria-controls={todoDialogId}
            >
              <StaySectionActionRing />
              To do
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoachTravelPaperStack({ ceremonyAndReception, revealed }) {
  const d = ceremonyAndReception
    ? weddingDetails.coachTravelCeremonyAndReception
    : weddingDetails.coachTravelReceptionOnly;
  const [taxisModalOpen, setTaxisModalOpen] = useState(false);
  const taxisDialogId = 'coach-travel-taxis-dialog';

  return (
    <>
      <TaxisModal
        open={taxisModalOpen}
        onClose={() => setTaxisModalOpen(false)}
        dialogId={taxisDialogId}
        faqMarkerSrc={sectionArt.faqMarker}
        tapeArtSrc={sectionArt.painterTape}
        paperTextureSrc={sectionArt.paperTexture}
      />
      <PaperStackReveal appearDelayMs={0} appearTilt="left" revealed={revealed}>
        <div className="coach-travel-section__paper-wrap">
          <div className="paper-stack-hover--card">
            <div className="coach-travel-paper-stack">
              <div className="coach-travel-paper-stack__sheet">
                <img
                  className="coach-travel-paper-stack__bg"
                  src={sectionArt.redPaper}
                  alt=""
                  width="1050"
                  height="2046"
                  decoding="async"
                />
                <div className="coach-travel-paper-stack__body">
                  <img
                    className="coach-travel-paper-stack__title-art"
                    src={sectionArt.weddingCoaches}
                    alt="Wedding coaches"
                    width="224"
                    height="79"
                    decoding="async"
                  />
                  <p className="coach-travel-paper-stack__intro">{d.intro}</p>
                  <div className="coach-travel-schedule">
                    {d.trips.map((trip, i) => (
                      <div key={`coach-trip-${i}`} className="coach-travel-schedule__block">
                        <p className="coach-travel-schedule__route">{trip.route}</p>
                        <p className="coach-travel-schedule__time">{trip.time}</p>
                      </div>
                    ))}
                  </div>
                  <p className="coach-travel-paper-stack__note">{d.note}</p>
                  <button
                    type="button"
                    className="coach-travel-paper-stack__taxis-btn"
                    onClick={() => setTaxisModalOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={taxisModalOpen}
                    aria-controls={taxisDialogId}
                  >
                    <span className="coach-travel-paper-stack__taxis-btn__ring" aria-hidden="true">
                      <svg
                        className="coach-travel-paper-stack__taxis-btn__svg"
                        viewBox="0 0 240 48"
                        preserveAspectRatio="none"
                        width="100%"
                        height="100%"
                      >
                        <ellipse
                          className="coach-travel-paper-stack__taxis-btn__ellipse"
                          cx="120"
                          cy="24"
                          rx="115"
                          ry="21"
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="1.25"
                          vectorEffect="nonScalingStroke"
                        />
                      </svg>
                    </span>
                    {"Local taxis"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PaperStackReveal>
    </>
  );
}

function PaperStackReveal({ children, appearDelayMs = 0, appearTilt = 'left', revealed }) {
  const tiltClass =
    appearTilt === 'right' ? 'paper-stack-appear--tilt-right' : 'paper-stack-appear--tilt-left';

  return (
    <div className="paper-stack-reveal-root">
      <div
        className={['paper-stack-appear', tiltClass, revealed && 'paper-stack-appear--visible']
          .filter(Boolean)
          .join(' ')}
        style={{ '--paper-appear-delay': `${appearDelayMs}ms` }}
      >
        {children}
      </div>
    </div>
  );
}

function CeremonyPaperStack({ keyPrefix = '', revealed, onSiteAccommodation = false }) {
  const k = (s) => (keyPrefix ? `${keyPrefix}-${s}` : s);
  const schedule = onSiteAccommodation
    ? weddingDetails.ceremonyScheduleOnSite
    : weddingDetails.ceremonySchedule;
  return (
    <PaperStackReveal appearDelayMs={0} appearTilt="left" revealed={revealed}>
      <div className="reception-section__paper-wrap reception-section__paper-wrap--ceremony">
        <div className="paper-stack-hover--card">
          <div className="ceremony-paper-stack">
            <div className="ceremony-paper-stack__sheet">
            <img
              className="ceremony-paper-stack__bg"
              src={sectionArt.blueNotebookPaper}
              alt=""
              width="576"
              height="1024"
              decoding="async"
            />
            <div className="ceremony-paper-stack__body">
              <img
                className="ceremony-paper-stack__title-art"
                src={sectionArt.theCeremony}
                alt="The Ceremony"
                width="189"
                height="76"
                decoding="async"
              />
              <div className="ceremony-schedule">
                {schedule.map((block, i) => (
                  <div key={k(`b-${i}`)} className="ceremony-schedule__block">
                    <p className="ceremony-schedule__eyebrow">{block.eyebrow}</p>
                    <div className="ceremony-schedule__lines">
                      {block.lines.map((line) => (
                        <p key={k(`line-${i}-${line}`)} className="ceremony-schedule__line">
                          {line}
                        </p>
                      ))}
                    </div>
                    {block.subline ? (
                      <p className="ceremony-schedule__subline">{block.subline}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </PaperStackReveal>
  );
}

const VIP_TICKET_ASPECT = 1410 / 590;

/** Lightbox motion: faster FLIP + quart in/out on move/scale; springy ease on rotate only. */
const VIP_LB_MOVE_MS = 400;
const VIP_LB_CLOSE_FALLBACK_MS = Math.round(VIP_LB_MOVE_MS * 1.35);
/** Approx. ease-in-out-quart (punchy symmetric). */
const VIP_LB_MOVE_EASE = 'cubic-bezier(0.76, 0, 0.24, 1)';
/** Slight overshoot past 0° then settle (physical tilt). */
const VIP_LB_ROT_EASE = 'cubic-bezier(0.33, 1.18, 0.45, 1)';

function getVipThumbRotationDeg() {
  if (typeof window === 'undefined') return -2.5;
  return window.matchMedia('(max-width: 640px)').matches ? -2 : -2.5;
}

/** FLIP metrics: expanded ticket fixed at viewport centre with size lastW × derived height. */
function computeVipLightboxFlip(thumbRect) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let lastW = Math.min(vw * 0.95, 980);
  let lastH = lastW / VIP_TICKET_ASPECT;
  if (lastH > vh * 0.86) {
    lastH = vh * 0.86;
    lastW = lastH * VIP_TICKET_ASPECT;
  }
  const lastCenterX = vw / 2;
  const lastCenterY = vh / 2;
  const firstCenterX = thumbRect.left + thumbRect.width / 2;
  const firstCenterY = thumbRect.top + thumbRect.height / 2;
  const sx = Math.max(thumbRect.width / lastW, 0.001);
  const sy = Math.max(thumbRect.height / lastH, 0.001);
  /* Uniform scale only — non-uniform scale(sx, sy) warps the box while the PNG stays correct
     aspect inside (object-fit), which reads as a glitch during the transition. */
  const s = Math.min(sx, sy);
  const dx = firstCenterX - lastCenterX;
  const dy = firstCenterY - lastCenterY;
  return { dx, dy, s, lastW, lastH, rot: getVipThumbRotationDeg() };
}

function ReceptionPaperStack({
  keyPrefix = '',
  paperWrapClassName = '',
  appearDelayMs = 0,
  appearTilt = 'left',
  revealed,
  ceremonyPlus = false,
  onSiteAccommodation = false,
  showVipTicket = false,
}) {
  const k = (s) => (keyPrefix ? `${keyPrefix}-${s}` : s);
  const schedule = ceremonyPlus
    ? weddingDetails.receptionScheduleCeremonyPlus
    : weddingDetails.receptionSchedule;

  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const [vipLb, setVipLb] = useState(null);
  const vipThumbRef = useRef(null);
  const vipFlyRef = useRef(null);
  const vipOpenAnimTokenRef = useRef(0);
  const vipHadLightboxRef = useRef(false);
  const vipDismissGuardUntilRef = useRef(0);

  const closeVipLightbox = useCallback(() => {
    if (reducedMotion) {
      setVipLb(null);
      return;
    }
    setVipLb((s) => {
      if (!s) return s;
      if (!s.expanded) {
        if (s.skipTransition) return null;
        return s;
      }
      const wrap = vipThumbRef.current;
      const img = wrap?.querySelector?.('.reception-paper-stack__vip-ticket');
      if (!wrap || !img) return null;
      const thumb = img.getBoundingClientRect();
      return { flip: computeVipLightboxFlip(thumb), expanded: false, skipTransition: false };
    });
  }, [reducedMotion]);

  useEffect(() => {
    if (vipHadLightboxRef.current && !vipLb) {
      vipDismissGuardUntilRef.current =
        typeof performance !== 'undefined'
          ? performance.now() + 420
          : Date.now() + 420;
    }
    vipHadLightboxRef.current = Boolean(vipLb);
  }, [vipLb]);

  const openVipLightbox = useCallback(() => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now < vipDismissGuardUntilRef.current) return;
    const wrap = vipThumbRef.current;
    const img = wrap?.querySelector?.('.reception-paper-stack__vip-ticket');
    if (!wrap || !img) return;
    const thumb = img.getBoundingClientRect();
    const flip = computeVipLightboxFlip(thumb);
    if (reducedMotion) {
      setVipLb({ flip, expanded: true, skipTransition: true });
    } else {
      setVipLb({ flip, expanded: false, skipTransition: true });
    }
  }, [reducedMotion]);

  /* Only the open FLIP “first frame” uses expanded:false + skipTransition:true. Closing uses expanded:false + skipTransition:false — must not re-run this or we snap back open. */
  useLayoutEffect(() => {
    if (!vipLb || vipLb.expanded || reducedMotion) return;
    if (!vipLb.skipTransition) return;
    const token = ++vipOpenAnimTokenRef.current;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (vipOpenAnimTokenRef.current !== token) return;
        setVipLb((s) =>
          s && !s.expanded && s.skipTransition ? { ...s, expanded: true, skipTransition: false } : s,
        );
      });
    });
    return () => cancelAnimationFrame(id);
  }, [vipLb, reducedMotion]);

  useEffect(() => {
    if (!vipLb) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeVipLightbox();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevOverflow) document.body.style.overflow = prevOverflow;
      else document.body.style.removeProperty('overflow');
    };
  }, [vipLb, closeVipLightbox]);

  /**
   * If transitionend never fires (Safari / subpixel), still tear down the portal.
   * Timer lives in this effect only — duplicate taps during close must NOT clear it
   * (that was leaving users stuck with no fallback).
   */
  useEffect(() => {
    if (!vipLb || vipLb.expanded || vipLb.skipTransition || reducedMotion) return undefined;
    const t = window.setTimeout(() => {
      setVipLb((cur) => {
        if (!cur || cur.expanded || cur.skipTransition) return cur;
        return null;
      });
    }, VIP_LB_CLOSE_FALLBACK_MS);
    return () => window.clearTimeout(t);
  }, [vipLb, reducedMotion]);

  const onVipLbFlyTransitionEnd = useCallback((e) => {
    if (e.target !== vipFlyRef.current) return;
    const p = e.propertyName;
    if (p !== 'transform' && p !== '-webkit-transform') return;
    setVipLb((s) => {
      if (!s || s.expanded) return s;
      return null;
    });
  }, []);

  const onVipLbPointerUp = useCallback(
    (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      closeVipLightbox();
    },
    [closeVipLightbox],
  );

  const vipMoveTransition =
    reducedMotion || vipLb?.skipTransition
      ? 'none'
      : `transform ${VIP_LB_MOVE_MS}ms ${VIP_LB_MOVE_EASE}`;
  const vipRotTransition =
    reducedMotion || vipLb?.skipTransition
      ? 'none'
      : `transform ${VIP_LB_MOVE_MS}ms ${VIP_LB_ROT_EASE}`;

  const vipLbPortal =
    vipLb && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={[
              'vip-ticket-lightbox',
              vipLb.expanded ? 'vip-ticket-lightbox--expanded' : '',
              reducedMotion ? 'vip-ticket-lightbox--reduced-motion' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="dialog"
            aria-modal="true"
            aria-label="VIP ticket"
            tabIndex={-1}
            onPointerUpCapture={onVipLbPointerUp}
            onClick={closeVipLightbox}
          >
            <div className="vip-ticket-lightbox__backdrop" aria-hidden />
            <div
              ref={vipFlyRef}
              className="vip-ticket-lightbox__fly"
              style={{
                width: vipLb.flip.lastW,
                marginLeft: -vipLb.flip.lastW / 2,
                marginTop: -vipLb.flip.lastH / 2,
                transition: vipMoveTransition,
                transform: vipLb.expanded
                  ? 'translate3d(0, 0, 0) scale(1, 1)'
                  : `translate3d(${vipLb.flip.dx}px, ${vipLb.flip.dy}px, 0) scale(${vipLb.flip.s}, ${vipLb.flip.s})`,
              }}
              onTransitionEnd={reducedMotion ? undefined : onVipLbFlyTransitionEnd}
            >
              <img
                className="vip-ticket-lightbox__img"
                src={sectionArt.vipTicket}
                alt=""
                width="1410"
                height="590"
                decoding="async"
                draggable={false}
                style={{
                  transition: vipRotTransition,
                  transform: vipLb.expanded ? 'rotate(0deg)' : `rotate(${vipLb.flip.rot}deg)`,
                }}
              />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <PaperStackReveal appearDelayMs={appearDelayMs} appearTilt={appearTilt} revealed={revealed}>
      <div
        className={['reception-section__paper-wrap', paperWrapClassName].filter(Boolean).join(' ')}
      >
        <div className="paper-stack-hover--card">
          <div className="reception-paper-stack">
            <div className="reception-paper-stack__sheet">
            <img
              className="reception-paper-stack__bg"
              src={sectionArt.notebookPaper}
              alt=""
              width="576"
              height="1024"
              decoding="async"
            />
            <div className="reception-paper-stack__body">
              <div className="reception-paper-stack__title-row">
                <img
                  className="reception-paper-stack__title-art"
                  src={sectionArt.theReception}
                  alt="The Reception"
                  width="189"
                  height="76"
                  decoding="async"
                />
              </div>
              <div className="reception-schedule">
                {schedule.map((block, i) =>
                  block.closing ? (
                    <p key={k(`c-${i}`)} className="reception-schedule__closing">
                      {onSiteAccommodation
                        ? weddingDetails.receptionScheduleClosingOnSite
                        : block.closing}
                    </p>
                  ) : (
                    <div key={k(`b-${i}`)} className="reception-schedule__block">
                      <p className="reception-schedule__eyebrow">{block.eyebrow}</p>
                      <div className="reception-schedule__lines">
                        {block.lines.map((line) => (
                          <p key={k(`line-${i}-${line}`)} className="reception-schedule__line">
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
              {showVipTicket ? (
                <>
                  <button
                    type="button"
                    ref={vipThumbRef}
                    className={[
                      'reception-paper-stack__vip-ticket-btn',
                      vipLb ? 'reception-paper-stack__vip-ticket-btn--concealed' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-expanded={Boolean(vipLb?.expanded)}
                    aria-haspopup="dialog"
                    aria-label="View VIP ticket full screen"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (vipLb?.expanded) return;
                      if (vipLb && !vipLb.expanded) return;
                      openVipLightbox();
                    }}
                  >
                    <img
                      className="reception-paper-stack__vip-ticket"
                      src={sectionArt.vipTicket}
                      alt="On-site guest VIP ticket"
                      width="1410"
                      height="590"
                      decoding="async"
                    />
                  </button>
                  {vipLbPortal}
                </>
              ) : null}
            </div>
          </div>
        </div>
        </div>
      </div>
    </PaperStackReveal>
  );
}

function parseCssLengthToPx(value, el) {
  if (!value || value === 'auto') return 0;
  const n = parseFloat(value);
  if (Number.isNaN(n)) return 0;
  if (value.endsWith('rem')) return n * 16;
  if (value.endsWith('em') && el) {
    const fs = parseFloat(getComputedStyle(el).fontSize) || 16;
    return n * fs;
  }
  if (value.endsWith('vh')) return (n / 100) * (typeof window !== 'undefined' ? window.innerHeight : 0);
  if (value.endsWith('px')) return n;
  return n;
}

function getInviteScrollTargetY() {
  const invite = document.getElementById('invite');
  if (!invite || typeof window === 'undefined') return 0;
  const cs = getComputedStyle(invite);
  const margin = parseCssLengthToPx(cs.scrollMarginTop, invite);
  const topDoc = invite.getBoundingClientRect().top + window.scrollY;
  return Math.max(1, topDoc - margin);
}

/** Scroll Y where the splash scroll CTA has just cleared the top of the viewport (+ small buffer). */
function getSplashCurtainSnapScrollY() {
  if (typeof window === 'undefined') return 0;
  const btn = document.getElementById('splash-scroll-prompt');
  if (btn) {
    const r = btn.getBoundingClientRect();
    const bottomDoc = r.bottom + window.scrollY;
    return Math.max(1, Math.ceil(bottomDoc + 18));
  }
  return getInviteScrollTargetY();
}

const CURTAIN_SCROLL_DURATION_MS = 920;

/** Wheel: only take over when still essentially at document top (matches touch “commit” band). */
const CURTAIN_SNAP_WHEEL_TOP_MAX_Y = 24;

/** First vertical finger move past this (px) triggers curtain snap on touch UI. */
const CURTAIN_TOUCH_SNAP_DRAG_PX = 10;

/** Smooth in-out with a soft settle (quart in-out). */
function curtainScrollEase(t) {
  return t < 0.5 ? 8 * t * t * t * t : 1 - (-2 * t + 2) ** 4 / 2;
}

let curtainScrollAnimCancel = null;

function cancelCurtainScrollAnimation() {
  curtainScrollAnimCancel?.();
  curtainScrollAnimCancel = null;
}

/**
 * Smooth window scroll with controlled duration (more gradual than native `smooth`).
 * Returns a cancel function.
 */
function animateWindowScrollTo(targetY, durationMs = CURTAIN_SCROLL_DURATION_MS, onDone) {
  if (typeof window === 'undefined') return () => {};
  cancelCurtainScrollAnimation();
  const startY = window.scrollY;
  const delta = targetY - startY;
  if (Math.abs(delta) < 0.5) {
    onDone?.();
    return () => {};
  }
  const t0 = performance.now();
  let rafId = 0;
  let cancelled = false;

  const tick = (now) => {
    if (cancelled) return;
    const u = Math.min(1, (now - t0) / durationMs);
    const y = startY + delta * curtainScrollEase(u);
    window.scrollTo(0, y);
    if (u < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      curtainScrollAnimCancel = null;
      onDone?.();
    }
  };

  const cancel = () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
    curtainScrollAnimCancel = null;
  };
  curtainScrollAnimCancel = cancel;
  rafId = requestAnimationFrame(tick);
  return cancel;
}

function isCurtainSnapTouchUi() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

/**
 * Skip splash curtain wheel/touch takeover when other UI is active.
 *
 * Document-level check: guest admin or any aria-modal (portaled VIP lightbox, CSV import, site
 * modals, /admin sign-in) must disable curtain entirely — at scrollY≈0, composedPath alone can
 * still let curtain preventDefault and kill nested scroll (e.g. guest list).
 *
 * Dev menu is only checked via event path: when expanded it stays mounted, so we must not use
 * querySelector('.dev-site-entry-menu') or we'd disable curtain for the whole page.
 */
function shouldSkipSplashCurtainForEvent(e) {
  if (typeof document !== 'undefined') {
    if (document.querySelector('.admin-guest-portal')) return true;
    if (document.querySelector('[aria-modal="true"]')) return true;
  }
  const touchesDevMenu = (n) => n && n.nodeType === 1 && n.classList?.contains('dev-site-entry-menu');
  if (typeof e.composedPath === 'function') {
    return e.composedPath().some(touchesDevMenu);
  }
  let el = e.target;
  if (el && el.nodeType !== Node.ELEMENT_NODE) el = el.parentElement;
  for (; el; el = el.parentElement) {
    if (touchesDevMenu(el)) return true;
  }
  return false;
}

function runCurtainSnapProgrammaticScroll(lockRef, destY) {
  lockRef.current = true;
  cancelCurtainScrollAnimation();
  animateWindowScrollTo(destY, CURTAIN_SCROLL_DURATION_MS, () => {
    lockRef.current = false;
  });
}

function scrollPastSplash() {
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const top = getSplashCurtainSnapScrollY();
  if (!top) {
    const invite = document.getElementById('invite');
    if (!invite) return;
    invite.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    return;
  }
  cancelCurtainScrollAnimation();
  if (reduceMotion) {
    window.scrollTo(0, top);
    return;
  }
  animateWindowScrollTo(top, CURTAIN_SCROLL_DURATION_MS, null);
}

function navHashActive() {
  if (typeof window === 'undefined') return '#about';
  const h = window.location.hash;
  return h && h !== '#' ? h : '#about';
}

function SiteNav({ includeCoachTravel = true }) {
  const navSectionIds = useMemo(
    () => getNavSectionIds(includeCoachTravel),
    [includeCoachTravel],
  );
  const [activeHash, setActiveHash] = useState(navHashActive);
  const [rsvpDashActive, setRsvpDashActive] = useState(false);
  const rsvpEllipseRef = useRef(null);
  const rsvpAnimRef = useRef(null);
  const rsvpHotPointer = useRef(false);
  const rsvpHotFocus = useRef(false);
  const scrollSpyPausedRef = useRef(false);

  const syncRsvpDash = () => {
    setRsvpDashActive(rsvpHotPointer.current || rsvpHotFocus.current);
  };

  const scrollToSection = (href) => {
    const id = href.replace(/^#/, '');
    const el = document.getElementById(id);
    if (!el) return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollSpyPausedRef.current = true;
    el.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    window.history.pushState(null, '', href);
    setActiveHash(href);
    window.setTimeout(() => {
      scrollSpyPausedRef.current = false;
    }, 900);
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    scrollToSection(href);
  };

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      if (!h || h === '#') {
        setActiveHash(computeActiveNavHash(navSectionIds));
        return;
      }
      setActiveHash(h);
      const id = h.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el && !scrollSpyPausedRef.current) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    };
    const onPopState = () => {
      const h = window.location.hash || '#about';
      setActiveHash(h);
      const id = h.replace(/^#/, '');
      const el = document.getElementById(id);
      if (!el) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scrollSpyPausedRef.current = true;
      el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => {
        scrollSpyPausedRef.current = false;
      }, 900);
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onPopState);
    };
  }, [navSectionIds]);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (scrollSpyPausedRef.current) return;
      const next = computeActiveNavHash(navSectionIds);
      setActiveHash((prev) => {
        if (prev === next) return prev;
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', next);
        }
        return next;
      });
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        tick();
      });
    };
    tick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [navSectionIds]);

  useEffect(() => {
    const el = rsvpEllipseRef.current;
    if (!rsvpDashActive) {
      rsvpAnimRef.current?.cancel();
      rsvpAnimRef.current = null;
      if (el) el.setAttribute('stroke-dashoffset', '0');
      return;
    }
    if (!el || typeof el.animate !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    rsvpAnimRef.current?.cancel();
    /* Dash period = sum(strokeDasharray) = 5+4; offset delta must be a multiple for a seamless loop. */
    const dashPeriod = 9;
    const laps = 11;
    const anim = el.animate(
      [{ strokeDashoffset: '0' }, { strokeDashoffset: String(-dashPeriod * laps) }],
      { duration: 6400, iterations: Infinity, easing: 'linear' }
    );
    rsvpAnimRef.current = anim;
    return () => {
      anim.cancel();
      rsvpAnimRef.current = null;
    };
  }, [rsvpDashActive]);

  const navLinkIsCurrent = (href) =>
    activeHash === href ||
    (includeCoachTravel && href === '#travel' && activeHash === '#coach-travel');

  const items = [
    { href: '#about', label: 'About', rsvp: false },
    { href: '#travel', label: 'Travel', rsvp: false },
    { href: '#stay', label: 'Stay', rsvp: false },
    { href: '#faqs', label: 'FAQs', rsvp: false },
    { href: '#rsvp', label: 'RSVP', rsvp: true },
  ];

  return (
    <nav
      className="site-nav"
      aria-label="Site"
      style={{ '--site-nav-hover-flower': `url('${sectionArt.hoverFlower}')` }}
    >
      <ul className="site-nav__list">
        {items.map(({ href, label, rsvp }) => {
          const current = navLinkIsCurrent(href);
          return (
            <li
              key={href}
              className={[
                'site-nav__item',
                current && 'site-nav__item--current',
                rsvp && 'site-nav__item--rsvp',
              ]
                .filter(Boolean)
                .join(' ')}
              data-rsvp-dash={rsvp ? (rsvpDashActive ? '1' : '0') : undefined}
            >
              <a
                href={href}
                className={rsvp ? 'site-nav__link site-nav__link--rsvp' : 'site-nav__link'}
                onClick={(e) => handleNavClick(e, href)}
                onMouseEnter={
                  rsvp
                    ? () => {
                        rsvpHotPointer.current = true;
                        syncRsvpDash();
                      }
                    : undefined
                }
                onMouseLeave={
                  rsvp
                    ? () => {
                        rsvpHotPointer.current = false;
                        syncRsvpDash();
                      }
                    : undefined
                }
                onFocus={
                  rsvp
                    ? () => {
                        rsvpHotFocus.current = true;
                        syncRsvpDash();
                      }
                    : undefined
                }
                onBlur={
                  rsvp
                    ? () => {
                        rsvpHotFocus.current = false;
                        syncRsvpDash();
                      }
                    : undefined
                }
              >
                {rsvp ? (
                  <span className="site-nav__link--rsvp__ring" aria-hidden="true">
                    <svg
                      className="site-nav__link--rsvp__svg"
                      viewBox="-8 0 144 48"
                      preserveAspectRatio="none"
                      width="100%"
                      height="100%"
                    >
                      <ellipse
                        ref={rsvpEllipseRef}
                        className="site-nav__link--rsvp__ellipse"
                        cx="64"
                        cy="24"
                        rx="70"
                        ry="19"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.85"
                        strokeDasharray="5 4"
                        strokeLinecap="round"
                        vectorEffect="nonScalingStroke"
                        pathLength="100"
                      />
                    </svg>
                  </span>
                ) : null}
                {label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function App() {
  const splashRef = useRef(null);
  const splashPhotoFrameRef = useRef(null);
  const curtainWheelLockRef = useRef(false);
  const paperStacksRef = useRef(null);
  const coachPaperStackRef = useRef(null);
  const [paperStacksInView, setPaperStacksInView] = useState(false);
  const [coachPaperStackInView, setCoachPaperStackInView] = useState(false);
  const [monogramHidden, setMonogramHidden] = useState(false);
  const [heroInviteRevealed, setHeroInviteRevealed] = useState(false);
  /** Increments each time scroll crosses from below → above half snap (remounts fireworks for replay). */
  const [heroFireworksSession, setHeroFireworksSession] = useState(0);
  const heroFireworksBelowHalfRef = useRef(true);
  const [accessGranted, setAccessGranted] = useState(() => !!readVerifiedGuestFromStorage());
  const [verifiedGuest, setVerifiedGuest] = useState(() => readVerifiedGuestFromStorage());
  const [registryRemoteReady, setRegistryRemoteReady] = useState(
    () => !isRemoteGuestRegistryConfigured() || isRemoteGuestRegistryHydrated(),
  );
  /** Bumped when URL path changes without a full page load (admin close, back/forward). */
  const [, setPathSync] = useState(0);
  const isDev = process.env.NODE_ENV === 'development';
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const onAdminRoute = isAdminPathname(pathname);
  const onVoiceNoteRoute = isVoiceNotePathname(pathname);
  const onHoneymoonFundRoute = isHoneymoonFundPathname(pathname);
  const [devSiteVisibilityMode, setDevSiteVisibilityModeState] = useState(() =>
    isDev ? readDevSiteVisibilityMode() : 'auto',
  );

  const effectiveSiteVisibility = resolveEffectiveSiteVisibility(
    isDev,
    devSiteVisibilityMode,
    verifiedGuest,
  );
  const showCeremonyContent = isCeremonyPlusSiteVisibility(effectiveSiteVisibility);
  const penicuikOnSiteAccommodation =
    effectiveSiteVisibility === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  const showCoachTravelSection = !penicuikOnSiteAccommodation;
  const penicuikHeroInvite = weddingDetails.heroPenicuikOnSiteInvite;

  const applyDevSiteVisibilityMode = useCallback((mode) => {
    writeDevSiteVisibilityMode(mode);
    setDevSiteVisibilityModeState(mode);
  }, []);

  const setDevEntry = useCallback((granted) => {
    if (granted) {
      persistDevBypassGuest();
      setAccessGranted(true);
      setVerifiedGuest(readVerifiedGuestFromStorage());
    } else {
      clearVerifiedGuestStorage();
      setAccessGranted(false);
      setVerifiedGuest(null);
    }
  }, []);

  useEffect(() => {
    preloadWeddingSiteAssets();
  }, []);

  useEffect(() => {
    const bump = () => setPathSync((n) => n + 1);
    window.addEventListener('popstate', bump);
    window.addEventListener(WEDDING_APP_PATH_CHANGED_EVENT, bump);
    return () => {
      window.removeEventListener('popstate', bump);
      window.removeEventListener(WEDDING_APP_PATH_CHANGED_EVENT, bump);
    };
  }, []);

  useEffect(() => {
    if (!isRemoteGuestRegistryConfigured()) return () => {};
    const unsub = startRemoteGuestRegistryListener();
    const syncReady = () => setRegistryRemoteReady(isRemoteGuestRegistryHydrated());
    syncReady();
    window.addEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, syncReady);
    return () => {
      unsub();
      window.removeEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, syncReady);
    };
  }, []);

  useEffect(() => {
    const refreshVerified = () => {
      if (!accessGranted) return;
      const v = readVerifiedGuestFromStorage();
      setVerifiedGuest(v);
      if (!v) {
        setAccessGranted(false);
      }
    };
    window.addEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, refreshVerified);
    return () => window.removeEventListener(WEDDING_GUEST_REGISTRY_CHANGE_EVENT, refreshVerified);
  }, [accessGranted]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!onHoneymoonFundRoute) return undefined;
    window.location.replace(WEDDING_MONZO_ME_URL);
    return undefined;
  }, [onHoneymoonFundRoute]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) {
      setPaperStacksInView(true);
      setCoachPaperStackInView(true);
      return;
    }
    const ioOpts = { threshold: 0.06, rootMargin: '0px 0px 12% 0px' };
    const observers = [];
    const watch = (ref, setSeen) => {
      const el = ref.current;
      if (!el) return;
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) setSeen(true);
      }, ioOpts);
      io.observe(el);
      observers.push(io);
    };
    watch(paperStacksRef, setPaperStacksInView);
    if (showCoachTravelSection) {
      watch(coachPaperStackRef, setCoachPaperStackInView);
    }
    return () => observers.forEach((io) => io.disconnect());
  }, [showCoachTravelSection]);

  useEffect(() => {
    if (!accessGranted) return;
    const el = splashPhotoFrameRef.current;
    if (!el) return;

    let pendingRaf = 0;
    const apply = () => {
      pendingRaf = 0;
      setMonogramHidden(el.getBoundingClientRect().top <= 0);
    };

    const schedule = () => {
      if (pendingRaf) return;
      pendingRaf = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const vv = window.visualViewport;
    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      if (pendingRaf) cancelAnimationFrame(pendingRaf);
    };
  }, [accessGranted]);

  useEffect(() => {
    if (!accessGranted) return;

    const splashEl = splashRef.current;

    const applyCurtain = () => {
      const end = getSplashCurtainSnapScrollY();
      const y = getScrollY();
      const t = Math.min(1, Math.max(0, y / end));
      splashEl?.style.setProperty('--splash-curtain', String(t));
    };

    let curtainRaf = 0;
    const scheduleCurtain = () => {
      if (curtainRaf) return;
      curtainRaf = requestAnimationFrame(() => {
        curtainRaf = 0;
        applyCurtain();
      });
    };

    applyCurtain();

    const mainEl = document.querySelector('.site-shell');
    const ro =
      typeof ResizeObserver !== 'undefined' && mainEl
        ? new ResizeObserver(() => scheduleCurtain())
        : null;
    ro?.observe(mainEl);

    window.addEventListener('scroll', scheduleCurtain, { passive: true });
    window.addEventListener('resize', scheduleCurtain, { passive: true });

    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', scheduleCurtain);
      window.removeEventListener('resize', scheduleCurtain);
      if (curtainRaf) cancelAnimationFrame(curtainRaf);
      splashEl?.style.removeProperty('--splash-curtain');
    };
  }, [accessGranted]);

  useEffect(() => {
    if (!accessGranted) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    const tick = () => {
      raf = 0;
      const snap = getSplashCurtainSnapScrollY();
      const y = getScrollY();
      if (reduceMotion) {
        setHeroInviteRevealed(y >= Math.max(0, snap - 48));
        setHeroFireworksSession(0);
        heroFireworksBelowHalfRef.current = true;
        return;
      }
      if (y >= snap - 12) {
        setHeroInviteRevealed(true);
      } else if (y < 56) {
        setHeroInviteRevealed(false);
      }
      const half = snap * 0.5;
      if (y < half) {
        heroFireworksBelowHalfRef.current = true;
      } else if (heroFireworksBelowHalfRef.current) {
        heroFireworksBelowHalfRef.current = false;
        setHeroFireworksSession((s) => s + 1);
      }
    };

    const onScrollOrResize = () => {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [accessGranted]);

  useEffect(() => {
    if (!accessGranted) {
      setHeroFireworksSession(0);
      heroFireworksBelowHalfRef.current = true;
    }
  }, [accessGranted]);

  /** Sticky nav: transparent in-flow bar; solid wine once pinned so fireworks sky shows through first. */
  useEffect(() => {
    if (!accessGranted) return undefined;
    const nav = document.querySelector('.site-nav');
    if (!nav) return undefined;

    let raf = 0;
    const tick = () => {
      raf = 0;
      const stuck = nav.getBoundingClientRect().top <= 0.5;
      nav.classList.toggle('site-nav--stuck', stuck);
    };
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
      nav.classList.remove('site-nav--stuck');
    };
  }, [accessGranted]);

  useEffect(() => {
    return () => {
      cancelCurtainScrollAnimation();
    };
  }, []);

  useEffect(() => {
    if (!accessGranted) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onWheel = (e) => {
      if (e.ctrlKey) return;
      if (shouldSkipSplashCurtainForEvent(e)) return;
      const dy = e.deltaY;
      if (Math.abs(dy) < Math.abs(e.deltaX)) return;

      const target = getSplashCurtainSnapScrollY();
      const y = getScrollY();
      const atOrPastSnap = y >= target - 2;

      if (curtainWheelLockRef.current) {
        e.preventDefault();
        return;
      }

      if (dy > 0 && y < CURTAIN_SNAP_WHEEL_TOP_MAX_Y && !atOrPastSnap) {
        e.preventDefault();
        runCurtainSnapProgrammaticScroll(curtainWheelLockRef, target);
        return;
      }

      if (dy < 0 && y > 0 && y <= target + 2) {
        e.preventDefault();
        runCurtainSnapProgrammaticScroll(curtainWheelLockRef, 0);
        return;
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [accessGranted]);

  /**
   * Touch / coarse pointer: snap on first deliberate vertical drag (Typeform-style), not after scroll
   * momentum ends. Same band as wheel: below snap → swipe down commits to hero; at hero top
   * (y ≤ snap + 2) → swipe up commits to splash top.
   */
  useEffect(() => {
    if (!accessGranted) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!isCurtainSnapTouchUi()) return;

    const lockRef = curtainWheelLockRef;
    let startFingerY = 0;
    let startFingerX = 0;
    let curtainArmed = false;
    let gestureConsumed = false;

    const resetGesture = () => {
      curtainArmed = false;
      gestureConsumed = false;
    };

    const onTouchStart = (e) => {
      if (lockRef.current) return;
      if (e.touches.length !== 1) return;
      if (shouldSkipSplashCurtainForEvent(e)) return;
      const snapY = getSplashCurtainSnapScrollY();
      const y = getScrollY();
      if (y > snapY + 2) {
        curtainArmed = false;
        return;
      }
      curtainArmed = true;
      gestureConsumed = false;
      const t = e.touches[0];
      startFingerY = t.clientY;
      startFingerX = t.clientX;
    };

    const onTouchMove = (e) => {
      if (!curtainArmed || gestureConsumed) return;
      if (e.touches.length !== 1) return;
      if (shouldSkipSplashCurtainForEvent(e)) {
        curtainArmed = false;
        return;
      }
      if (lockRef.current) {
        e.preventDefault();
        return;
      }

      const t = e.touches[0];
      const dy = startFingerY - t.clientY;
      const dx = t.clientX - startFingerX;
      if (Math.abs(dy) < CURTAIN_TOUCH_SNAP_DRAG_PX) return;
      if (Math.abs(dx) > Math.abs(dy) * 1.12) {
        curtainArmed = false;
        return;
      }

      const snapY = getSplashCurtainSnapScrollY();
      const y = getScrollY();

      if (dy > 0) {
        if (y >= snapY - 2) {
          curtainArmed = false;
          return;
        }
        e.preventDefault();
        gestureConsumed = true;
        curtainArmed = false;
        runCurtainSnapProgrammaticScroll(lockRef, snapY);
        return;
      }

      if (y <= 4) {
        curtainArmed = false;
        return;
      }
      if (y <= snapY + 2) {
        e.preventDefault();
        gestureConsumed = true;
        curtainArmed = false;
        runCurtainSnapProgrammaticScroll(lockRef, 0);
        return;
      }
      curtainArmed = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', resetGesture, { passive: true, capture: true });
    document.addEventListener('touchcancel', resetGesture, { passive: true, capture: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('touchend', resetGesture, { capture: true });
      document.removeEventListener('touchcancel', resetGesture, { capture: true });
    };
  }, [accessGranted]);

  return (
    <>
      {isDev ? (
        <DevSiteEntryMenu
          accessGranted={accessGranted}
          onSetDevEntry={setDevEntry}
          devSiteVisibilityMode={devSiteVisibilityMode}
          onSetDevSiteVisibilityMode={applyDevSiteVisibilityMode}
          verifiedSiteVisibility={
            verifiedGuest?.siteVisibility ?? SITE_VISIBILITY.RECEPTION_ONLY
          }
        />
      ) : null}
      {!accessGranted && !onAdminRoute && !onVoiceNoteRoute && !onHoneymoonFundRoute && (
        <EmailGate
          registryLoading={isRemoteGuestRegistryConfigured() && !registryRemoteReady}
          onGranted={() => {
            setAccessGranted(true);
            setVerifiedGuest(readVerifiedGuestFromStorage());
          }}
        />
      )}
      <ProductionGuestAdmin />
      {onHoneymoonFundRoute ? null : onVoiceNoteRoute ? (
        <VoiceNotePage />
      ) : accessGranted ? (
    <main className="site-shell site-shell--gate-reveal">
      <section ref={splashRef} className="splash" id="splash" aria-label="Welcome">
        <div
          className={[
            'splash-monogram-fixed',
            monogramHidden && 'splash-monogram-fixed--hidden',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          <div className="splash-entrance splash-entrance--monogram">
            <img
              src={sectionArt.splashMonogram}
              alt=""
              width={143}
              height={298}
              decoding="async"
            />
          </div>
        </div>
        <div className="splash-body">
          <div className="splash-curtain-depth splash-curtain-depth--photo">
            <div
              className="splash-photo-frame splash-entrance splash-entrance--photo"
              ref={splashPhotoFrameRef}
            >
              <img
                className="splash-photo"
                src={sectionArt.splashPhoto}
                alt="Meg & Ben"
              />
            </div>
          </div>
          <div className="splash-curtain-depth splash-curtain-depth--script">
            <SplashScotlandScript />
          </div>
        </div>
        <button
          type="button"
          id="splash-scroll-prompt"
          className="splash-scroll splash-entrance splash-entrance--scroll splash-curtain-depth splash-curtain-depth--scroll"
          onClick={scrollPastSplash}
          aria-label="Scroll to site content"
        >
          <img src={sectionArt.splashScrollFlower} alt="" className="splash-scroll-flower" />
          <div className="splash-scroll-label-slot">
            <span className="splash-scroll-label splash-scroll-label--exit">Scroll</span>
            <span className="splash-scroll-label splash-scroll-label--enter">Scroll</span>
          </div>
          <img
            src={sectionArt.splashScrollFlower}
            alt=""
            className="splash-scroll-flower splash-scroll-flower--mirror"
          />
        </button>
      </section>

      {heroFireworksSession > 0 ? (
        <HeroFireworks key={heroFireworksSession} />
      ) : null}
      <section className="hero-invite" id="invite" aria-label="Wedding invitation">
        <div
          className={[
            'hero-invite__inner',
            heroInviteRevealed && 'hero-invite__inner--revealed',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <img
            src={sectionArt.heroTop}
            alt="Join us on"
            className="hero-invite__script hero-invite__reveal hero-invite__reveal--join"
          />
          <img
            src={sectionArt.heroTitle}
            alt="New Year's Eve"
            className="hero-invite__title-svg hero-invite__reveal hero-invite__reveal--title"
          />
          <img
            src={sectionArt.heroBottom}
            alt="To celebrate"
            className="hero-invite__script hero-invite__reveal hero-invite__reveal--celebrate"
          />
          <div className="hero-invite__details hero-invite__reveal hero-invite__reveal--copy">
            {penicuikOnSiteAccommodation ? (
              <div className="hero-invite__penicuik">
                <header className="hero-invite__penicuik-header">
                  {penicuikHeroInvite.introParagraphs.map((text, i) => (
                    <p key={i} className="hero-invite__penicuik-16 hero-invite__penicuik-intro-p">
                      {text}
                    </p>
                  ))}
                </header>
                <img
                  src={sectionArt.splitter}
                  alt=""
                  className="hero-invite__penicuik-splitter"
                  width="22"
                  height="20"
                  decoding="async"
                  aria-hidden="true"
                />
                <div className="hero-invite__penicuik-schedule">
                  {penicuikHeroInvite.schedule.map((block) => (
                    <div key={block.day} className="hero-invite__penicuik-block">
                      <p className="hero-invite__penicuik-12">{block.day}</p>
                      {block.lines.map((line) => (
                        <p key={line} className="hero-invite__penicuik-16 hero-invite__penicuik-schedule-line">
                          {line}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="hero-invite__copy">
                {showCeremonyContent
                  ? weddingDetails.heroInviteCeremonyAndReception
                  : weddingDetails.heroInviteReceptionOnly}
              </p>
            )}
          </div>
        </div>
      </section>

      <SiteNav includeCoachTravel={showCoachTravelSection} />

      <section id="about" className="about-section" aria-label="About">
        <section className="estate-hero" aria-label="Penicuik Estate">
          <div className="estate-hero__media">
            <img
              className="estate-hero__photo"
              src={sectionArt.penicuikCourtyard}
              alt="Penicuik Estate courtyard with fountain, lawns, and stone buildings"
            />
          </div>
        </section>

        <section className="reception-section" aria-label="Reception">
          <div className="reception-section__intro" id="intro">
            <div className="reception-section__intro-inner">
              {(showCeremonyContent
                ? weddingDetails.estateStoryCeremonyAndReception
                : weddingDetails.estateStoryReceptionOnly
              ).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div ref={paperStacksRef} className="reception-section__paper-stacks">
            {showCeremonyContent ? (
              <CeremonyPaperStack
                keyPrefix="ceremony"
                revealed={paperStacksInView}
                onSiteAccommodation={penicuikOnSiteAccommodation}
              />
            ) : null}
            <ReceptionPaperStack
              keyPrefix="reception"
              paperWrapClassName={
                showCeremonyContent ? 'reception-section__paper-wrap--overlap-reception' : ''
              }
              appearDelayMs={showCeremonyContent ? 32 : 0}
              appearTilt={showCeremonyContent ? 'right' : 'left'}
              revealed={paperStacksInView}
              ceremonyPlus={showCeremonyContent}
              onSiteAccommodation={penicuikOnSiteAccommodation}
              showVipTicket={penicuikOnSiteAccommodation}
            />
          </div>
        </section>
      </section>

      <section className="travel-section" id="travel" aria-label="Travel">
        <div className="travel-section__inner">
          <div className="travel-section__intro">
            <header className="travel-section__header">
              <img
                src={sectionArt.travelTitle}
                alt="Travel"
                className="travel-section__title-art"
                decoding="async"
              />
            </header>
            <div className="travel-section__address">
              <p>
                {weddingDetails.travelAddress.map((line, i) => (
                  <Fragment key={line}>
                    {i > 0 && <br />}
                    {line}
                  </Fragment>
                ))}
              </p>
            </div>
            <a
              href={TRAVEL_MAPS_URL}
              className="travel-section__maps-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="travel-section__maps-btn__ring" aria-hidden="true">
                <svg
                  className="travel-section__maps-btn__svg"
                  viewBox="0 0 240 48"
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                >
                  <ellipse
                    className="travel-section__maps-btn__ellipse"
                    cx="120"
                    cy="24"
                    rx="115"
                    ry="21"
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    vectorEffect="nonScalingStroke"
                  />
                </svg>
              </span>
              Google Maps
            </a>
          </div>
          <div className="travel-section__modes" role="presentation">
            {weddingDetails.travelModes.map((mode) => (
              <div key={mode.key} className="travel-section__mode">
                <h3 className="travel-section__mode-title">
                  <img
                    src={mode.key === 'train' ? sectionArt.travelTrain : sectionArt.travelPlane}
                    alt={mode.key === 'train' ? 'Train' : 'Plane'}
                    className="travel-section__mode-wordmark"
                    decoding="async"
                  />
                </h3>
                <div className="travel-section__mode-body">
                  <p>{mode.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="travel-section__footnote" role="note">
            {weddingDetails.travelFootnoteLines.map((line, i) => (
              <Fragment key={`travel-foot-${i}`}>
                {i > 0 ? (
                  <img
                    src={sectionArt.faqDivider}
                    alt=""
                    className="travel-section__footnote-divider"
                    width="18"
                    height="15"
                    decoding="async"
                    aria-hidden="true"
                  />
                ) : null}
                <p className="travel-section__footnote-line">{line}</p>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {showCoachTravelSection ? (
        <section className="coach-travel-section" id="coach-travel" aria-label="Coach travel">
          <div ref={coachPaperStackRef} className="coach-travel-section__paper-stacks">
            <CoachTravelPaperStack
              ceremonyAndReception={showCeremonyContent}
              revealed={coachPaperStackInView}
            />
          </div>
        </section>
      ) : null}

      <StaySection penicuikOnSiteAccommodation={penicuikOnSiteAccommodation} />

      <FaqSection
        penicuikOnSiteAccommodation={penicuikOnSiteAccommodation}
        showCeremonyFaq={showCeremonyContent}
        verifiedGuest={verifiedGuest}
      />

      <RsvpSection verifiedGuest={verifiedGuest} />
      <SiteFooter />
    </main>
      ) : null}
    </>
  );
}

export default App;
